import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "../authorization/authService";

export const fileApi = createApi({
  reducerPath: "fileApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["File", "FileCategory", "MirroredFiles"],
  endpoints: (builder) => ({
    getFilesByApplicationId: builder.query({
      query: (applicationId) => ({
        url: `/application_file/${applicationId}/files`,
        method: "GET",
      }),
      providesTags: (result, error, applicationId) => [
        { type: "File", applicationId },
      ],
    }),
    getFileCategories: builder.query({
      query: () => ({
        url: `/application_file/categories`,
        method: "GET",
      }),
      providesTags: ["FileCategory"],
    }),
    // The server's own answer to "may this be disbursed yet". Tagged File, so
    // every upload/delete/update below re-asks it — the rule lives on the
    // server and the UI only renders what comes back.
    getDisbursementReadiness: builder.query({
      query: (applicationId) => ({
        url: `/application_file/${applicationId}/disbursement-readiness`,
        method: "GET",
      }),
      providesTags: (result, error, applicationId) => [
        { type: "File", applicationId },
      ],
    }),
    createFileWithUpload: builder.mutation({
      query: ({ applicationId, fileData }) => ({
        url: `/application_file/${applicationId}/create_with_file`,
        method: "POST",
        body: fileData,
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: "File", applicationId },
        { type: "MirroredFiles", id: "ALL" },
        { type: "MirroredFiles", id: `ALL-${applicationId}` },
        // Invalidate all specific template-application combinations for this app
        { type: "MirroredFiles", id: `${applicationId}` },
      ],
      // Immediate optimistic update
      async onQueryStarted({ applicationId }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          // Dispatch immediate sync event for MirroredFilesDisplay
          window.dispatchEvent(
            new CustomEvent("file-operation", {
              detail: { type: "file-uploaded", applicationId },
            })
          );
        } catch {
          // Handle error if needed
        }
      },
    }),
    deleteFile: builder.mutation({
      query: ({ fileId }) => ({
        url: `/application_file/file/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: "File", applicationId },
        { type: "MirroredFiles", id: "ALL" },
        { type: "MirroredFiles", id: `ALL-${applicationId}` }, // Specific app targeting
      ],
      // Optimistic update for immediate UI feedback
      async onQueryStarted({ fileId, applicationId }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          // Dispatch immediate sync event for MirroredFilesDisplay
          window.dispatchEvent(
            new CustomEvent("file-operation", {
              detail: { type: "file-deleted", applicationId, fileId },
            })
          );
        } catch {
          // Handle error if needed
        }
      },
    }),
    updateFile: builder.mutation({
      query: ({ fileId, formData }) => ({
        url: `/application_file/file/${fileId}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: "File", applicationId },
        { type: "MirroredFiles", id: "ALL" },
        { type: "MirroredFiles", id: `ALL-${applicationId}` },
      ],
      async onQueryStarted({ fileId, applicationId }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          window.dispatchEvent(
            new CustomEvent("file-operation", {
              detail: { type: "file-updated", applicationId, fileId },
            })
          );
        } catch {
          // Handle error if needed
        }
      },
    }),
  }),
});

export const {
  useGetFilesByApplicationIdQuery,
  useGetFileCategoriesQuery,
  useGetDisbursementReadinessQuery,
  useCreateFileWithUploadMutation,
  useDeleteFileMutation,
  useUpdateFileMutation,
} = fileApi;
